/**
 * `/workflows/deal-screening/runs/:runId` — one deal's memo.
 *
 * The page the workflow exists to produce: the opportunity memo, every
 * sentence and every number traceable to the page or cell it came from, the
 * run's own report on what it read, the gaps and conflicts a person has to
 * settle, and "Mark reviewed" once nothing blocks it.
 *
 * Structure worth knowing:
 *  - The tab lives in `?tab=`, so a colleague can be sent straight to the gaps.
 *  - Every write answers with the whole recomputed run (the server re-derives
 *    gaps, block groups and questions), and the page takes that answer as its
 *    state — it never patches a fact client-side.
 *  - A refetch (a generating run's poll) holds the memo on screen and dims it.
 *  - A citation opens its file in the viewer with the passage highlighted.
 *    A web source opens in a new tab.
 */

import { stripMarks } from "../../components/DealScreening/inlineMarks";
import React, {
	Suspense,
	lazy,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import PageTabs from "../../components/Dashboards/PageTabs";
import { runHref } from "../../components/DealScreening/DealsTable";
import { dealScreeningChipLabel } from "../../components/DealScreening/dealScreeningChatBridge";
import { reviewItems } from "../../components/DealScreening/dealScreeningReview";
import GapsView from "../../components/DealScreening/GapsView";
import MemoRunHeader from "../../components/DealScreening/MemoRunHeader";
import type { EditItem } from "../../components/DealScreening/sectionEditModel";
import {
	EMPTY_FIGURES,
	type FiguresDraft,
	dealRename,
	figuresFromStored,
	figuresPayload,
} from "../../components/DealScreening/figuresEditModel";
import {
	clearMemoEditDraft,
	draftDoc,
	editSections,
	readMemoEditDraft,
	startMemoDoc,
	writeMemoEditDraft,
} from "../../hooks/useMemoEditDraft";
import type { CodeBlockRenderers } from "../../components/Chat/codeBlockRenderers";
import {
	type DocumentSource,
	type KnownSources,
	documentSourceAnchor,
} from "../../components/DealScreening/documentSource";
import { draftSummary } from "../../components/DealScreening/memoDraftSummary";
import MemoEditProposalCard from "../../components/DealScreening/MemoEditProposalCard";
import {
	type PlacedEdit,
	type ProposalContext,
	applyEdit,
	factsWith,
	figuresWith,
	parseEdits,
	placeEdit,
} from "../../components/DealScreening/memoEditProposal";
import {
	type AppliedProposal,
	appliedHere,
	blockKey,
	changedSectionKey,
	proposalVersion,
	readApplied,
	sectionsPayload,
	versionEdits,
	writeApplied,
} from "../../components/DealScreening/memoVersionSave";
import {
	type AddedSection,
	isEditableSection,
	splitMemoDoc,
	withSectionDoc,
} from "../../components/DealScreening/memoEditorModel";
import {
	FINANCIAL_COLUMNS,
	FINANCIAL_ROWS,
	orderSetting,
} from "../../components/DealScreening/tableOrder";
import ShareMemoDialog, {
	type ShareMemoOptions,
} from "../../components/DealScreening/ShareMemoDialog";
import MemoView, { sectionElementId } from "../../components/DealScreening/MemoView";
import {
	companyFactsTitle,
	type SourceTarget,
	distinctSources,
	sourceNumbers,
} from "../../components/DealScreening/memoModel";
import RunReportView from "../../components/DealScreening/RunReportView";
import VersionsView from "../../components/DealScreening/VersionsView";
import SourcesView from "../../components/DealScreening/SourcesView";
import ErrorDisplay from "../../components/ErrorDisplay";
import { PdfViewerProvider } from "../../context/PdfViewerContext";
import { SpreadsheetViewerProvider } from "../../context/SpreadsheetViewerContext";
import { useToast } from "../../context/ToastContext";
import { useDealScreeningRun } from "../../hooks/useDealScreeningRun";
import { useDopplerUriOpener } from "../../hooks/useDopplerUriOpener";
import {
	DealScreeningConflictError,
	dealScreeningService,
} from "../../services/dealScreeningService";
import type {
	DealScreeningClearFactPayload,
	DealScreeningFact,
	DealScreeningResolveConflictPayload,
	DealScreeningRunVersion,
	DealScreeningSetFactPayload,
	DealScreeningSource,
} from "../../types/dealScreening";
import { friendlyErrorMessage } from "../../utils/apiErrorDetail";
import { useOptionalAuthUser } from "../../context/AuthContext";
import MemoSavedBanner, { lastSeenVersion, rememberSeenVersion } from "../../components/DealScreening/MemoSavedBanner";
import MemoShareFolder from "../../components/DealScreening/MemoShareFolder";
import RailFallback from "../../components/Chat/RailFallback";
import { useRailThread } from "../../hooks/useRailThread";
import { RUN_THREAD_PARAM } from "../../utils/chatHref";

/** Loaded when edit mode opens: the editor (TipTap) is not a small import. */
const MemoEditMode = lazy(() => import("../../components/DealScreening/MemoEditMode"));

/** Loaded only when the rail is opened: the chat page is not a small import. */
const DealScreeningAnalystRail = lazy(
	() => import("../../components/DealScreening/DealScreeningAnalystRail"),
);

/**
 * What every Analyst turn carries, typed off the rail's own props so this page
 * does not import the context type from a file DOP-499 owns.
 */
type RailContext = ReturnType<
	React.ComponentProps<
		typeof import("../../components/DealScreening/DealScreeningAnalystRail").default
	>["buildContext"]
>;

/**
 * Four tabs (DOP-616): Memo, Gaps, Sources read (the run report and every
 * file and web page, DOP-645) and Versions. The old Run report tab folded
 * into it, so a stale `?tab=report` link opens Sources read.
 */
type RunTab = "memo" | "gaps" | "sources" | "history";

const TAB_IDS: RunTab[] = ["memo", "gaps", "sources", "history"];

/** The mark on the memo element a jump from Sources read lands on. */
const JUMP_MARK = ["rounded", "bg-[#E6F3F3]", "ring-2", "ring-[#99CFCD]", "transition-colors"];

const isRunTab = (value: string | null): value is RunTab =>
	value !== null && (TAB_IDS as string[]).includes(value);

/**
 * Whether two `memo-edit` blocks are the same block. The chat escapes a `$`
 * before a digit in what it renders, so a block reaches its card as "\\$2.25"
 * and sits in the answer's text as "$2.25".
 */
const sameBlock = (a: string, b: string): boolean => {
	const bare = (text: string) => text.replace(/\\\$/g, "$").replace(/\s+/g, "");
	return bare(a) === bare(b);
};

/** A 409 is the server's own sentence and is shown verbatim. */
const messageOf = (caught: unknown, fallback: string): string =>
	caught instanceof DealScreeningConflictError
		? caught.message
		: friendlyErrorMessage(caught, fallback);

const DealScreeningRunView: React.FC = () => {
	const { runId } = useParams<{ runId: string }>();
	const [searchParams, setSearchParams] = useSearchParams();
	const navigate = useNavigate();
	const { showToast } = useToast();
	const openUri = useDopplerUriOpener();

	const { run, loading, error, stale, refresh, replace } =
		useDealScreeningRun(runId);

	// A thread's link names the version its last turn was asked on. Opened
	// from the sidebar later, the memo it means is the deal's newest (DOP-653):
	// a change applied since is there, not in the version the turn saw.
	// History's links to older versions carry no thread, so they stay put.
	const fromThread = searchParams.get(RUN_THREAD_PARAM) !== null;
	const newestId = run?.latest_run_id ?? null;
	useEffect(() => {
		if (!fromThread || !run || !newestId || newestId === run.public_id) return;
		navigate(`/workflows/deal-screening/runs/${encodeURIComponent(newestId)}?${searchParams.toString()}`, {
			replace: true,
		});
	}, [fromThread, run, newestId, navigate, searchParams]);

	const rawTab = searchParams.get("tab");
	const tabParam = rawTab === "report" ? "sources" : rawTab;
	const tab: RunTab = isRunTab(tabParam) ? tabParam : "memo";
	const setTab = useCallback(
		(next: RunTab) => {
			const params = new URLSearchParams(searchParams);
			params.set("tab", next);
			setSearchParams(params, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	// The memo's Edit on a person's answer lands on the Gaps tab with that
	// answer open (DOP-555). Carried in the URL so the tab switch keeps it.
	const editKey = searchParams.get("edit");
	const editTarget = useMemo(
		() => (editKey ? { key: editKey, period: searchParams.get("edit_period") } : null),
		[editKey, searchParams],
	);
	// A conflicted value in the memo opens its conflict on the Gaps tab,
	// where the sources sit side by side and a person chooses.
	const conflictKey = searchParams.get("conflict");
	const conflictTarget = useMemo(
		() =>
			conflictKey
				? { key: conflictKey, period: searchParams.get("conflict_period") }
				: null,
		[conflictKey, searchParams],
	);
	const handleOpenConflict = useCallback(
		(fact: DealScreeningFact) => {
			const params = new URLSearchParams(searchParams);
			params.set("tab", "gaps");
			params.delete("edit");
			params.delete("edit_period");
			params.set("conflict", fact.key);
			if (fact.period) params.set("conflict_period", fact.period);
			else params.delete("conflict_period");
			setSearchParams(params, { replace: true });
		},
		[searchParams, setSearchParams],
	);
	const handleEditFact = useCallback(
		(fact: DealScreeningFact) => {
			const params = new URLSearchParams(searchParams);
			params.set("tab", "gaps");
			params.delete("conflict");
			params.delete("conflict_period");
			params.set("edit", fact.key);
			if (fact.period) params.set("edit_period", fact.period);
			else params.delete("edit_period");
			setSearchParams(params, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	const [sectionKey, setSectionKey] = useState<string | null>(null);

	// ---------------------------------------------------------------- analyst
	const {
		threadId: threadParam,
		open: analystOpen,
		setOpen: setAnalystOpen,
		nameThread,
		close: closeAnalyst,
	} = useRailThread(RUN_THREAD_PARAM);

	// -------------------------------------------------------------- versions
	const runPublicId = run?.public_id;
	const runVersion = run?.version;
	const [versions, setVersions] = useState<DealScreeningRunVersion[]>([]);
	const [versionsLoading, setVersionsLoading] = useState(false);
	const [versionsError, setVersionsError] = useState<Error | undefined>(undefined);
	const historyOpen = tab === "history";
	useEffect(() => {
		if (!runPublicId || !historyOpen) return;
		let cancelled = false;
		setVersionsLoading(true);
		void (async () => {
			try {
				const next = await dealScreeningService.listRunVersions(runPublicId);
				if (!cancelled) {
					setVersions(next);
					setVersionsError(undefined);
				}
			} catch (caught) {
				if (!cancelled) {
					setVersionsError(caught instanceof Error ? caught : new Error(String(caught)));
				}
			} finally {
				if (!cancelled) setVersionsLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, [runPublicId, historyOpen, runVersion]);

	// ------------------------------------------------------ shared memos (co-editing)
	const user = useOptionalAuthUser();
	// The memo is shared and edited together when the flag is on and the
	// server says what this reader may do with it.
	const coediting = Boolean(run?.my_access);
	const canEditMemo = !coediting || run?.my_access !== "view";
	const me = useMemo(
		() => ({ id: user?.id ?? "", name: (user?.full_name || user?.email || "You").trim() }),
		[user?.id, user?.full_name, user?.email],
	);
	// Versions are read on open too: the banner says who saved since this
	// person last looked, and the comparison opens on it.
	const memoDealId = run?.deal.public_id;
	useEffect(() => {
		if (!coediting || !runPublicId || historyOpen) return;
		let cancelled = false;
		void dealScreeningService
			.listRunVersions(runPublicId)
			.then((next) => {
				if (!cancelled) setVersions(next);
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
		};
	}, [coediting, runPublicId, historyOpen, runVersion]);
	const [seenVersion] = useState<number | null>(() => (memoDealId ? lastSeenVersion(memoDealId) : null));
	const [bannerDismissed, setBannerDismissed] = useState(false);
	useEffect(() => {
		// Once this version is on screen, it is the one they last looked at.
		if (memoDealId && run && !run.generating && run.latest_version) rememberSeenVersion(memoDealId, run.latest_version);
	}, [memoDealId, run]);
	const [compareFrom, setCompareFrom] = useState<number | null>(null);
	const [sharingFolder, setSharingFolder] = useState(false);

	// ----------------------------------------------------------------- writes
	const handleSetFact = useCallback(
		async (factKey: string, payload: DealScreeningSetFactPayload) => {
			if (!runPublicId || !run) return false;
			const blockedBefore = run.blocking_open;
			try {
				const next = await dealScreeningService.setFact(runPublicId, factKey, payload);
				replace(next);
				showToast(
					blockedBefore > 0 && next.blocking_open === 0
						? "Saved. The memo is no longer blocked."
						: "Saved.",
					"success",
				);
				return true;
			} catch (caught) {
				showToast(messageOf(caught, "Could not save the value."), "error");
				return false;
			}
		},
		[run, runPublicId, replace, showToast],
	);

	const handleClearFact = useCallback(
		async (factKey: string, payload: DealScreeningClearFactPayload) => {
			if (!runPublicId) return false;
			try {
				replace(await dealScreeningService.clearFact(runPublicId, factKey, payload));
				showToast("Back to Doppler's value. Your decision is kept in the history.", "success");
				return true;
			} catch (caught) {
				showToast(messageOf(caught, "Could not clear the decision."), "error");
				return false;
			}
		},
		[runPublicId, replace, showToast],
	);

	const handleRevertDecision = useCallback(
		async (decisionId: string) => {
			if (!runPublicId) return false;
			try {
				replace(await dealScreeningService.revertDecision(runPublicId, decisionId));
				showToast("Reverted.", "success");
				return true;
			} catch (caught) {
				showToast(messageOf(caught, "Could not revert the decision."), "error");
				return false;
			}
		},
		[runPublicId, replace, showToast],
	);

	const handleResolveConflict = useCallback(
		async (factKey: string, payload: DealScreeningResolveConflictPayload) => {
			if (!runPublicId) return false;
			try {
				replace(await dealScreeningService.resolveConflict(runPublicId, factKey, payload));
				showToast("Choice recorded.", "success");
				return true;
			} catch (caught) {
				showToast(messageOf(caught, "Could not record the choice."), "error");
				return false;
			}
		},
		[runPublicId, replace, showToast],
	);

	const [reviewing, setReviewing] = useState(false);
	const handleReview = useCallback(async () => {
		if (!runPublicId) return;
		setReviewing(true);
		try {
			replace(await dealScreeningService.reviewRun(runPublicId));
			showToast("Marked reviewed.", "success");
		} catch (caught) {
			showToast(messageOf(caught, "Could not mark the memo reviewed."), "error");
		} finally {
			setReviewing(false);
		}
	}, [runPublicId, replace, showToast]);

	const [sharing, setSharing] = useState(false);
	const [exporting, setExporting] = useState(false);
	const handleExport = useCallback(
		async ({ format }: ShareMemoOptions) => {
			if (!runPublicId) return;
			setExporting(true);
			try {
				const file =
					format === "pdf"
						? await dealScreeningService.exportRunPdf(runPublicId)
						: await dealScreeningService.exportRunDocx(runPublicId);
				dealScreeningService.saveExportFile(file);
				setSharing(false);
			} catch (caught) {
				showToast(messageOf(caught, "Could not export the memo."), "error");
			} finally {
				setExporting(false);
			}
		},
		[runPublicId, showToast],
	);

	// ------------------------------------------------------------- edit mode
	const [editing, setEditing] = useState(false);
	// `?join=1`: straight into the shared draft (a page sent here by a save
	// elsewhere, so the editor follows the draft to its new base).
	const joinParam = searchParams.get("join") === "1";
	useEffect(() => {
		if (!coediting || !joinParam || !run || run.generating) return;
		setEditing(true);
		const params = new URLSearchParams(searchParams);
		params.delete("join");
		setSearchParams(params, { replace: true });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [coediting, joinParam, run?.public_id]);
	const [editNotice, setEditNotice] = useState<string | null>(null);
	// The financial summary row edit mode opens on, from a Gaps item (DOP-650).
	const [editFocus, setEditFocus] = useState<string | null>(null);
	useEffect(() => {
		// Edit mode left by any door (saved, stopped): the next opens at the top.
		if (!editing) setEditFocus(null);
	}, [editing]);
	const [savingVersion, setSavingVersion] = useState(false);
	const handleSaveVersion = useCallback(
		async (
			name: string,
			itemsBySection: Record<string, EditItem[]>,
			sectionKeys: string[],
			figures: FiguresDraft,
			titles: Record<string, string>,
			sections: { added: AddedSection[]; removed: string[] },
		) => {
			if (!run) return;
			setSavingVersion(true);
			try {
				// A section typed in re-sends its whole script; one only renamed
				// (the financial summary, say) sends the rename (DOP-641).
				const edits = versionEdits(run, itemsBySection, sectionKeys, titles);
				// The figures typed, and the sections added or taken out (DOP-656).
				const payload = { ...figuresPayload(figures), ...sectionsPayload(sections.added, sections.removed) };
				// The deal's name is the deal's (DOP-641): every version and the
				// deals list read it. A rename alone makes no new version; with
				// other edits it is saved with them, in one transaction, so a
				// save that fails renames nothing (codex r1).
				const rename = dealRename(figures);
				if (edits.length === 0 && Object.keys(payload).length === 0) {
					if (rename) await dealScreeningService.updateDeal(run.deal.public_id, { name: rename });
					clearMemoEditDraft(run.public_id);
					setEditing(false);
					showToast(`Renamed the deal to ${rename}.`, "success");
					refresh();
					return;
				}
				const body = {
					name: name.trim() || null,
					edits,
					...payload,
					...(rename ? { deal_name: rename } : {}),
					expected_latest_version: run.latest_version,
				};
				// Together, the shared draft is what is saved: the server records
				// who typed what and the draft moves on to the new version.
				const saved = coediting
					? await dealScreeningService.saveMemoDraftVersion(run.deal.public_id, body)
					: await dealScreeningService.saveVersion(run.public_id, body);
				if (!coediting) clearMemoEditDraft(run.public_id);
				setEditing(false);
				showToast(`Saved as version ${saved.version}.`, "success");
				navigate(runHref(saved.public_id));
			} catch (caught) {
				showToast(messageOf(caught, "Could not save the new version."), "error");
			} finally {
				setSavingVersion(false);
			}
		},
		[run, navigate, showToast, refresh, coediting],
	);

	// The newest version the person has been shown: the history's, once listed.
	const latestVersion = versions.length
		? Math.max(...versions.map((v) => v.version))
		: run?.latest_version;
	/** Bring an earlier version back as the newest one; nothing is overwritten (DOP-623). */
	const handleRestoreVersion = useCallback(
		async (versionId: string) => {
			try {
				const restored = await dealScreeningService.saveVersion(versionId, {
					edits: [],
					restore: true,
					expected_latest_version: latestVersion,
				});
				showToast(`Restored as version ${restored.version}.`, "success");
				navigate(runHref(restored.public_id));
			} catch (caught) {
				showToast(messageOf(caught, "Could not restore that version."), "error");
			}
		},
		[navigate, showToast, latestVersion],
	);

	const [stopping, setStopping] = useState(false);
	const handleStop = useCallback(async () => {
		if (!runPublicId) return;
		setStopping(true);
		try {
			replace(await dealScreeningService.stopRun(runPublicId));
			showToast("Stopped. The deal's last memo is unchanged.", "success");
		} catch (caught) {
			showToast(messageOf(caught, "Could not stop the run."), "error");
		} finally {
			setStopping(false);
		}
	}, [runPublicId, replace, showToast]);

	const dealPublicId = run?.deal.public_id;
	const [rerunning, setRerunning] = useState(false);
	const handleRunAgain = useCallback(async () => {
		if (!dealPublicId) return;
		setRerunning(true);
		try {
			const result = await dealScreeningService.runDeal(dealPublicId);
			navigate(runHref(result.run.public_id));
		} catch (caught) {
			showToast(messageOf(caught, "Could not start the run."), "error");
		} finally {
			setRerunning(false);
		}
	}, [dealPublicId, navigate, showToast]);

	// -------------------------------------------------------------- evidence
	const handleOpenSource = useCallback(
		(source: DealScreeningSource) => {
			// A web source links out (DOP-581); there is no viewer to open.
			if (source.kind === "web" && (source.url || source.anchor)) {
				window.open(source.url || source.anchor, "_blank", "noopener,noreferrer");
				return;
			}
			openUri(source.anchor, { filename: source.filename });
		},
		[openUri],
	);

	const sources = useMemo(() => (run ? distinctSources(run) : []), [run]);

	// From a cited place in Sources read to where the memo uses it (Turtleback
	// 10-03): the memo tab, scrolled to the section or the company fact, which
	// is marked for a moment so the eye finds it.
	const [expandFactKey, setExpandFactKey] = useState<string | null>(null);
	const handleJump = useCallback(
		(target: SourceTarget) => {
			setExpandFactKey(target.expandFact ?? null);
			setTab("memo");
			// Once the memo has been drawn.
			window.setTimeout(() => {
				const element = document.getElementById(target.elementId);
				if (!element) return;
				element.scrollIntoView?.({ behavior: "smooth", block: "center" });
				element.classList.add(...JUMP_MARK);
				window.setTimeout(() => element.classList.remove(...JUMP_MARK), 2400);
			}, 150);
		},
		[setTab],
	);
	const numbers = useMemo(() => (run ? sourceNumbers(run) : new Map<string, number>()), [run]);
	const numberOf = useCallback(
		(anchor: string | null | undefined) => (anchor ? (numbers.get(anchor) ?? null) : null),
		[numbers],
	);
	const numberOfRef = useRef(numberOf);
	numberOfRef.current = numberOf;

	/**
	 * What the memo chat's proposals are placed against (DOP-653): the memo
	 * as edit mode opens it, its figures, and the names of its files.
	 */
	const proposalContext = useMemo<ProposalContext | null>(
		() =>
			run
				? {
						sections: editSections(run),
						facts: run.facts,
						figures: run.figures,
						order: {
							columns: orderSetting(run.conventions, FINANCIAL_COLUMNS),
							rows: orderSetting(run.conventions, FINANCIAL_ROWS),
						},
						isEditable: isEditableSection,
						companyFactsTitle: companyFactsTitle(run),
					}
				: null,
		[run],
	);
	/** A file's name by its id, from the files the run read and the sources it cites. */
	const filenames = useMemo(() => {
		const names = new Map<number, string>();
		run?.report.files.forEach((file) => names.set(file.attachment_id, file.filename));
		sources.forEach(({ source }) => {
			if (source.attachment_id > 0 && !names.has(source.attachment_id)) {
				names.set(source.attachment_id, source.filename);
			}
		});
		return names;
	}, [run, sources]);
	/** The memo's files and the sources it cites, for reading a proposal's. */
	const knownSources = useMemo<KnownSources>(() => {
		const cited = new Map(sources.map(({ source }) => [source.anchor, source]));
		return {
			filenameOf: (id) => filenames.get(id) ?? null,
			byAnchor: (anchor) => cited.get(anchor) ?? null,
		};
	}, [filenames, sources]);

	// The proposals applied to this deal's memo, and the ones being saved now.
	const dealId = run?.deal.public_id;
	const [appliedProposals, setAppliedProposals] = useState<Record<string, AppliedProposal>>({});
	const [applyingKeys, setApplyingKeys] = useState<string[]>([]);
	useEffect(() => {
		setAppliedProposals(dealId ? readApplied(dealId) : {});
	}, [dealId]);

	/**
	 * Changes the memo chat proposed, applied (DOP-618, DOP-653).
	 *
	 * Applying is the decision, so the change is saved as the memo's next
	 * version and the memo on screen becomes that version, in place: the
	 * chat stays open and nothing reloads. The version it was applied to is
	 * untouched.
	 *
	 * A person with unsaved edits of their own gets the change put in with
	 * them and edit mode opened on it: saving the change alone would leave
	 * their edits behind, and saving both would save what they had not
	 * finished.
	 */
	const handleApplyProposals = useCallback(
		async (entries: { key: string; placed: PlacedEdit }[]) => {
			if (!runPublicId || !run || !proposalContext || entries.length === 0) return;
			// A change from the chat goes into the memo as it is now. Applied to
			// an earlier version it would become the newest without everything
			// saved since (DOP-653): the newest is where it is applied.
			if (run.latest_run_id && run.latest_run_id !== run.public_id) {
				showToast(
					`This is version ${run.version}; version ${run.latest_version} is the newest. Open it to apply the change.`,
					"error",
				);
				return;
			}
			const changes = entries.map((entry) => entry.placed);
			const keys = entries.map((entry) => entry.key);
			const stored = readMemoEditDraft(runPublicId);
			if (draftSummary(run, numberOfRef.current, stored) !== null) {
				const start = startMemoDoc(run, numberOfRef.current);
				const applied = changes.reduce(
					(draft, placed) =>
						applyEdit(
							proposalContext,
							draft,
							placed,
							(doc, key) => splitMemoDoc(doc)[key],
							withSectionDoc,
						),
					{ doc: draftDoc(stored, start) ?? start, figures: figuresFromStored(stored?.figures) },
				);
				writeMemoEditDraft(runPublicId, applied.doc, applied.figures);
				setAnalystOpen(false);
				setEditNotice(
					changes.length === 1
						? "The suggested change is in with your unsaved edits, marked in purple. Review, then save as a new version."
						: `The ${changes.length} suggested changes are in with your unsaved edits, marked in purple. Review, then save as a new version.`,
				);
				setEditing(true);
				return;
			}
			const payload = proposalVersion(run, numberOfRef.current, proposalContext, changes);
			if (!payload) {
				showToast("The memo already reads this way. Nothing was changed.", "info");
				return;
			}
			setApplyingKeys(keys);
			try {
				const saved = await dealScreeningService.saveVersion(run.public_id, {
					...payload,
					expected_latest_version: run.latest_version,
				});
				setAppliedProposals(
					writeApplied(run.deal.public_id, keys, { version: saved.version, runId: saved.public_id }),
				);
				// The answer is the new version, whole: it becomes the memo on
				// screen, and only then is it named in the URL.
				replace(saved);
				const params = new URLSearchParams(searchParams);
				params.set("tab", "memo");
				navigate(`${runHref(saved.public_id)}?${params.toString()}`, { replace: true });
				showToast(
					changes.length === 1
						? `Applied. Saved as version ${saved.version}.`
						: `Applied ${changes.length} changes. Saved as version ${saved.version}.`,
					"success",
				);
				const section = changes.map(changedSectionKey).find((key) => key !== null);
				// Once the new version has been drawn.
				window.setTimeout(() => {
					const target = section ? document.getElementById(sectionElementId(section)) : null;
					target?.scrollIntoView?.({ behavior: "smooth", block: "start" });
				}, 150);
			} catch (caught) {
				showToast(messageOf(caught, "Could not apply the change."), "error");
			} finally {
				setApplyingKeys([]);
			}
		},
		[
			runPublicId,
			run,
			proposalContext,
			replace,
			navigate,
			searchParams,
			showToast,
			setAnalystOpen,
		],
	);
	/** A document a proposal names, opened where it is cited. */
	const handleOpenProposalSource = useCallback(
		(source: DocumentSource) =>
			openUri(documentSourceAnchor(source), { filename: source.filename ?? undefined }),
		[openUri],
	);
	const codeRenderers = useMemo<CodeBlockRenderers>(
		() => ({
			"memo-edit": (code, { streaming, siblings }) => {
				const stored = runPublicId ? readMemoEditDraft(runPublicId) : null;
				// Unsaved edits of the person's own: the change goes in with them.
				const drafting =
					run !== null && run !== undefined && draftSummary(run, numberOfRef.current, stored) !== null;
				// Placed against what it will be applied to: the person's draft
				// when they have one, the version on screen when they do not.
				const figuresNow = drafting ? figuresFromStored(stored?.figures) : EMPTY_FIGURES;
				const at = siblings.findIndex((block) => sameBlock(block, code));
				// This block among the answer's, so it carries the rows the
				// answer named; on its own while the answer is still arriving.
				const blocks = at >= 0 ? siblings : [code];
				const edits = parseEdits(blocks, knownSources);
				const index = at >= 0 ? at : 0;
				const key = blockKey(code);
				const edit = edits[index];
				const placed = edit && proposalContext ? placeEdit(proposalContext, edit, figuresNow) : null;
				// Applied before, and still so on the version on screen: one
				// that does not have the change can take it (codex r3).
				const appliedOn = (eachKey: string, found: PlacedEdit | null): AppliedProposal | null => {
					const record = appliedProposals[eachKey] ?? null;
					if (!run || !proposalContext) return record;
					if (record) {
						return appliedHere(record, run, numberOfRef.current, proposalContext, found) ? record : null;
					}
					// No record on this computer (another browser applied it, or
					// storage is off), but the memo on screen already reads this
					// way: it is in, whichever version put it there.
					const inMemo =
						!drafting &&
						found !== null &&
						!found.problem &&
						proposalVersion(run, numberOfRef.current, proposalContext, [found]) === null;
					return inMemo ? { version: run.version, runId: run.public_id } : null;
				};
				// Together, each is placed against the draft the ones before it
				// leave: a value may sit under a column the block above added.
				const all: { key: string; placed: PlacedEdit }[] = [];
				if (proposalContext && at === siblings.length - 1 && siblings.length > 1) {
					let figures = figuresNow;
					edits.forEach((each, position) => {
						const eachKey = blockKey(blocks[position]);
						const found = each ? placeEdit(proposalContext, each, figures) : null;
						if (!found || found.problem) return;
						if (appliedOn(eachKey, found)) return;
						all.push({ key: eachKey, placed: found });
						if (found.kind === "figures") figures = figuresWith(proposalContext, figures, found.proposal).draft;
						if (found.kind === "company_facts") figures = factsWith(proposalContext, figures, found.proposal);
					});
				}
				return (
					<MemoEditProposalCard
						placed={placed}
						pending={streaming && placed === null}
						onApply={(change) => void handleApplyProposals([{ key, placed: change }])}
						onOpenSource={handleOpenProposalSource}
						applied={appliedOn(key, placed)}
						applying={applyingKeys.length > 0}
						mode={drafting ? "edit" : "save"}
						baseVersion={run?.version ?? null}
						applyAll={
							!streaming && all.length > 1
								? { count: all.length, onApply: () => void handleApplyProposals(all) }
								: null
						}
					/>
				);
			},
		}),
		[
			runPublicId,
			run,
			proposalContext,
			knownSources,
			handleApplyProposals,
			handleOpenProposalSource,
			appliedProposals,
			applyingKeys,
		],

	);

	const gapsCount = useMemo(() => (run ? reviewItems(run).length : 0), [run]);

	const tabs = useMemo(
		() => [
			{ id: "memo", label: "Memo" },
			// One name with the workflow's tab that lists the same items.
			{ id: "gaps", label: "Gaps", count: gapsCount },
			// Files and web pages alike (DOP-645): each is a source the run read.
			{ id: "sources", label: "Sources read", count: run?.report.files.length ?? 0 },
			{ id: "history", label: "Versions" },
		],
		[gapsCount, run?.report.files.length],
	);

	if (loading && run === null) {
		return (
			<div className="h-full bg-gray-50 px-6 py-6">
				<div className="h-[420px] animate-pulse rounded-lg border border-gray-200 bg-white" />
			</div>
		);
	}

	if (run === null) {
		return (
			<div className="h-full bg-gray-50">
				<ErrorDisplay
					error={error ?? new Error("This memo could not be loaded.")}
					onRetry={() => void refresh()}
				/>
			</div>
		);
	}

	// Read at send time: what the person has typed and not saved (DOP-654),
	// so a change is proposed against the memo as it reads on their screen.
	const buildContext = (): RailContext => {
		const draft = draftSummary(run, numberOf, readMemoEditDraft(run.public_id));
		return {
			run_id: run.public_id,
			tab,
			section_key: sectionKey,
			...(draft ? { draft } : {}),
		};
	};

	if (editing) {
		return (
			<Suspense fallback={<div className="h-full bg-white" />}>
				<MemoEditMode
					run={run}
					numberOf={numberOf}
					saving={savingVersion}
					onSave={(name, items, keys, figures, titles, sections) =>
						void handleSaveVersion(name, items, keys, figures, titles, sections)
					}
					onExit={() => {
						setEditing(false);
						setEditNotice(null);
					}}
					notice={editNotice}
					focusFigure={editFocus}
					coedit={coediting ? { me, canEdit: canEditMemo } : null}
					onRebase={(runId) => navigate(`${runHref(runId)}?join=1`)}
				/>
			</Suspense>
		);
	}

	return (
		<div className="flex h-full min-h-0 min-w-0 flex-1 overflow-hidden bg-[#f7f7f6]">
			<div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
				<MemoRunHeader
					run={run}
					fromTab={searchParams.get("from")}
					citations={numbers.size}
					onOpenGaps={() => setTab("gaps")}
					analystOpen={analystOpen}
					onToggleAnalyst={() => {
									setAnalystOpen((open) => !open);
					}}
					onShare={() => (coediting && run.my_access === "owner" ? setSharingFolder(true) : setSharing(true))}
					onDownload={coediting && run.my_access === "owner" ? () => setSharing(true) : undefined}
					editLabel={coediting ? (canEditMemo ? "Join editing" : "Follow the draft") : undefined}
					access={coediting ? run.my_access : undefined}
					onStop={() => void handleStop()}
					stopping={stopping}
					onEdit={() => {
									setAnalystOpen(false);
						setEditing(true);
					}}
					onReview={() => void handleReview()}
					reviewing={reviewing}
					onRunAgain={() => void handleRunAgain()}
					rerunning={rerunning}
				/>

				<PageTabs
					tabs={tabs}
					activeId={tab}
					onSelect={(id) => setTab(id as RunTab)}
					ariaLabel="Run sections"
				/>
				{coediting && !bannerDismissed && (
					<MemoSavedBanner
						run={run}
						versions={versions}
						meId={me.id}
						seenVersion={seenVersion}
						onShowChanges={(fromVersion) => {
							setCompareFrom(fromVersion);
							setTab("history");
						}}
						onJoin={() => {
							setAnalystOpen(false);
							setEditing(true);
						}}
						onDismiss={() => setBannerDismissed(true)}
					/>
				)}

				{error && (
					<p role="alert" className="px-6 pt-3 text-sm text-red-600">
						Could not reload this memo — what is shown may be out of date.{" "}
						<button
							type="button"
							onClick={() => void refresh()}
							className="underline hover:no-underline"
						>
							Try again
						</button>
					</p>
				)}

				<div
					className={`min-h-0 flex-1 overflow-auto px-6 pb-6 pt-4 ${
						stale ? "pointer-events-none opacity-60" : ""
					}`}
					data-testid="deal-screening-run-body"
				>
					{tab === "memo" && (
						<MemoView
							run={run}
							onOpenSource={handleOpenSource}
							activeSectionKey={sectionKey}
							onSelectSection={setSectionKey}
							onEditFact={handleEditFact}
							onOpenConflict={handleOpenConflict}
							expandFactKey={expandFactKey}
						/>
					)}
					{tab === "gaps" && (
						<GapsView
							key={
								editTarget
									? `edit:${editTarget.key}@${editTarget.period ?? ""}`
									: conflictTarget
										? `conflict:${conflictTarget.key}@${conflictTarget.period ?? ""}`
										: "gaps"
							}
							run={run}
							editTarget={editTarget}
							conflictTarget={conflictTarget}
							onSetFact={handleSetFact}
							onResolveConflict={handleResolveConflict}
							onClearFact={handleClearFact}
							onRevertDecision={handleRevertDecision}
							onOpenSource={handleOpenSource}
							onFillInSummary={(key) => {
								const label = run.facts.find((fact) => fact.key === key)?.label ?? "The figure";
								setAnalystOpen(false);
								setEditFocus(key);
								setEditNotice(`Type ${label} for each year, with where it came from, then save a new version.`);
								setEditing(true);
							}}
							onWriteSection={(key) => {
								const title = stripMarks(run.sections.find((section) => section.key === key)?.title ?? "The section");
								setAnalystOpen(false);
								setEditNotice(`${title} is set to appear in every memo. Write it, then save a new version.`);
								setEditing(true);
							}}
						/>
					)}
					{tab === "sources" && (
						<div className="mx-auto flex max-w-[960px] flex-col gap-6">
							<RunReportView run={run} onOpenGaps={() => setTab("gaps")} />
							<SourcesView
								files={run.report.files}
								sources={sources}
								numberOf={numberOf}
								onOpenSource={handleOpenSource}
								onJump={handleJump}
								folderName={run.deal.folder_name}
							/>
						</div>
					)}
					{tab === "history" && (
						<VersionsView
							versions={versions}
							loading={versionsLoading}
							onRestore={canEditMemo ? handleRestoreVersion : undefined}
							error={versionsError}
							currentRun={run}
							loadRun={dealScreeningService.getRun}
							meId={coediting ? me.id : undefined}
							compareFrom={compareFrom}
						/>
					)}
				</div>
			</div>

			{sharingFolder && (
				<MemoShareFolder folderId={run.deal.folder_id} dealName={run.deal.name} onClose={() => setSharingFolder(false)} />
			)}
			{sharing && (
				<ShareMemoDialog
					run={run}
					downloading={exporting}
					onDownload={(options) => void handleExport(options)}
					onClose={() => setSharing(false)}
				/>
			)}

			{analystOpen && (
				<Suspense fallback={<RailFallback />}>
					<DealScreeningAnalystRail
						contextLabel={dealScreeningChipLabel(run)}
						buildContext={buildContext}
						prefill={null}
						onClose={closeAnalyst}
						initialChatId={threadParam}
						onThreadChange={nameThread}
						codeRenderers={codeRenderers}
					/>
				</Suspense>
			)}
		</div>
	);
};

const DealScreeningRunPage: React.FC = () => (
	<SpreadsheetViewerProvider>
		<PdfViewerProvider>
			<DealScreeningRunView />
		</PdfViewerProvider>
	</SpreadsheetViewerProvider>
);

export default DealScreeningRunPage;
